import { type CSSProperties, useEffect, useId, useRef } from "react";

/**
 * The after-sign-in splash for the staff apps (GMS, OVR): the app's WSL
 * lockup draws itself on a dark stage — the ribbon's outline traces on, the
 * colour wipes in behind it, the app's word and tagline rise, a light sweeps
 * across — then the stage folds down onto the sidebar (taking the sidebar's
 * own colour, so it lands seamlessly) while the lockup flies into the
 * sidebar's brand slot, uncovering the page from right to left.
 *
 * The page mounts and loads behind it, so the ~2 s covers the first fetches
 * rather than adding to them. A click or any key skips straight to the fold.
 * Reduced motion: no splash. No strings (the lockup is artwork), so this
 * stays translation-free like the rest of the package.
 *
 * The app marks its sidebar `data-brand-splash-target` and the brand logo in
 * it `data-brand-splash-mark`; with no visible sidebar (the phone drawer)
 * the stage simply fades.
 *
 * Arming: the sign-in page calls `armBrandSplash()` when it lets someone in;
 * the shell reads `brandSplashArmed()` in its state initialiser and calls
 * `disarmBrandSplash()` in an effect, so a reload or a remount never replays
 * it. In memory on purpose: it belongs to that one sign-in.
 */

let armed = false;
export const armBrandSplash = () => {
	armed = true;
};
export const brandSplashArmed = () => armed;
export const disarmBrandSplash = () => {
	armed = false;
};

/** The ribbon on the 2001 × 751 artboard of the WSL logo files (the portal's WslMark geometry). */
const MARK = "M464.39,155.02s0-.06.03-.06h104.92c12,0,21.67,4.67,29,14l79.96,97.96s.05.02.06,0l28.97-56.95c7.33-15.33,16-24.66,26-28,4-.67,8.66-1,14-1h58c12,0,21.67,4.67,29,14l105,116c5.33,6,12,9,20,9h183.55c6.9,0,12.84-5.4,12.95-12.3.11-6.99-5.53-12.7-12.5-12.7h-152c-34,0-60-20-60-56v-27.96c0-34.81,28.22-63.04,63.04-63.04h266.96c12,0,21.33,5,28,15l108,146c3.33,4.67,8,7,14,7,5.5,0,57.03.02,61.75.03,2.04,0,6.42.06,11.43,2.11,4.49,1.84,7.61,4.5,9.43,6.3,11.79,15.52,23.59,31.04,35.38,46.56,3.82,4.87,6.28,8,6.28,8h-176.28c-9.33,0-17-4-23-12l-118.99-157.99s-.02,0-.03,0h-169.54c-7.83,0-14.58,6.01-14.93,13.83-.37,8.31,6.26,15.17,14.49,15.17h130c40,0,69,29,69,66v15c0,35-27,60-61,60h-274c-10,0-18-3.67-24-11l-93.97-93.97s-.04-.01-.06,0l-58.98,88.96c-6.67,10.67-15.67,16-27,16h-44c-10.67,0-19-4-25-12l-173.95-211.94h.03Z";
/** The darker fold inside the W (TSR's favicon geometry), placed on the same artboard by FOLD_AT. */
const FOLD =
	"M28.9877 6.39001C28.8973 6.3197 28.5524 6.0708 28.0691 6.1054C27.7588 6.12772 27.5334 6.25719 27.4262 6.32974C26.2453 8.64686 25.0656 10.964 23.8847 13.2811C26.3291 16.3092 28.7723 19.3362 31.2167 22.3643L36.7204 14.0624C34.1432 11.5042 31.566 8.94711 28.9888 6.3889H28.9877V6.39001Z";
const FOLD_AT = "translate(464.39 147.97) scale(8.962)";
/** The ribbon's box in MARK's own coordinates (the primary logo). */
const RIBBON = { x: 464, y: 148, w: 1072, h: 231 };

type Rect = { x: number; y: number; w: number; h: number };
type Tone = {
	/** Where the ribbon sits in this app's artwork, relative to the primary logo. */
	shift: { x: number; y: number };
	word: Rect;
	tagline: Rect;
	view: Rect;
	stops: [number, string][];
	highlight: string;
	fold: [string, string];
	line: string;
	glow: string;
};

/** Measured off the WSL GMS / WSL OVR logo files (same 2001 × 751 artboard). */
const TONES = {
	gms: {
		shift: { x: -207, y: 43 },
		word: { x: 1356, y: 313, w: 402, h: 109 },
		tagline: { x: 354, y: 495, w: 1291, h: 52 },
		view: { x: 240, y: 176, w: 1536, h: 388 },
		stops: [
			[0, "#3a73ff"],
			[1, "#3438f0"],
		],
		highlight: "#2a9cff",
		fold: ["#3a0ed6", "#2d48e8"],
		line: "#8fb4ff",
		glow: "rgba(45, 79, 232, 0.55)",
	},
	ovr: {
		shift: { x: -197, y: 43 },
		word: { x: 1366, y: 313, w: 387, h: 109 },
		tagline: { x: 576, y: 495, w: 848, h: 52 },
		view: { x: 250, y: 176, w: 1520, h: 388 },
		stops: [
			[0, "#a97cf2"],
			[1, "#a557ee"],
		],
		highlight: "#cfa8ff",
		fold: ["#9c36e8", "#a56eee"],
		line: "#dcc4ff",
		glow: "rgba(122, 69, 216, 0.55)",
	},
} satisfies Record<string, Tone>;

export type BrandSplashTone = keyof typeof TONES;

export interface BrandSplashProps {
	tone: BrandSplashTone;
	/** The app's word ("GMS") and tagline, white on transparent, cut from its logo file. */
	wordSrc: string;
	taglineSrc: string;
	onDone: () => void;
}

const EASE_OUT = "cubic-bezier(.22, 1, .36, 1)";
const EASE_IN_OUT = "cubic-bezier(.65, .02, .2, 1)";
const FOLD_EASE = "cubic-bezier(.76, 0, .24, 1)";
/** When the fold onto the sidebar starts, and how long it takes (ms). */
const EXIT_AT = 1500;
const EXIT_MS = 640;
const FADE_MS = 180;

export function BrandSplash({ tone, wordSrc, taglineSrc, onDone }: BrandSplashProps) {
	const t: Tone = TONES[tone];
	const uid = `bs${useId().replace(/[^\w-]/g, "")}`;
	const root = useRef<HTMLDivElement>(null);
	const navy = useRef<HTMLDivElement>(null);
	const lockup = useRef<HTMLDivElement>(null);
	const line = useRef<SVGPathElement>(null);
	const fill = useRef<SVGGElement>(null);
	const sheen = useRef<SVGRectElement>(null);
	const word = useRef<SVGImageElement>(null);
	const tagline = useRef<SVGImageElement>(null);
	const done = useRef(onDone);
	done.current = onDone;

	useEffect(() => {
		const el = root.current;
		if (!el) return;
		if (typeof el.animate !== "function" || matchMedia("(prefers-reduced-motion: reduce)").matches) {
			done.current();
			return;
		}

		const running: Animation[] = [];
		const play = (node: Element | null, frames: Keyframe[], opts: KeyframeAnimationOptions) => {
			if (!node) return null;
			const a = node.animate(frames, { fill: "both", ...opts });
			running.push(a);
			return a;
		};

		// ── the draw ──────────────────────────────────────────────────────────
		const intro = [
			play(lockup.current, [{ opacity: 0, transform: "scale(.96)" }, { opacity: 1, transform: "none" }], {
				duration: 500,
				easing: EASE_OUT,
			}),
			play(line.current, [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], { duration: 820, easing: EASE_IN_OUT }),
			play(line.current, [{ opacity: 1 }, { opacity: 0 }], { delay: 760, duration: 380 }),
			play(fill.current, [{ clipPath: "inset(0 100% 0 0)" }, { clipPath: "inset(0 0% 0 0)" }], {
				delay: 220,
				duration: 720,
				easing: EASE_OUT,
			}),
			play(word.current, [{ opacity: 0, transform: "translateX(-48px)" }, { opacity: 1, transform: "none" }], {
				delay: 640,
				duration: 520,
				easing: EASE_OUT,
			}),
			play(tagline.current, [{ opacity: 0, transform: "translateY(26px)" }, { opacity: 0.9, transform: "none" }], {
				delay: 780,
				duration: 520,
				easing: EASE_OUT,
			}),
			play(sheen.current, [{ transform: "translateX(0)" }, { transform: `translateX(${RIBBON.w + 560}px)` }], {
				delay: 880,
				duration: 660,
				easing: "cubic-bezier(.45, 0, .25, 1)",
			}),
		];

		// ── the fold onto the sidebar ─────────────────────────────────────────
		let exiting = false;
		let timer = 0;
		const exit = () => {
			if (exiting) return;
			exiting = true;
			window.clearTimeout(timer);
			for (const a of intro) a?.finish();

			const vw = window.innerWidth;
			const vh = window.innerHeight;
			const panel = document.querySelector<HTMLElement>("[data-brand-splash-target]");
			const p = panel?.getBoundingClientRect();

			let last: Animation | null;
			if (panel && p && p.width > 8 && p.right > 8 && p.left < vw) {
				// the stage takes the sidebar's own colour as it shrinks onto it
				if (navy.current) navy.current.style.background = getComputedStyle(panel).backgroundColor;
				play(navy.current, [{ opacity: 0 }, { opacity: 1 }], { duration: EXIT_MS * 0.7, easing: "ease-in" });
				play(el, [{ clipPath: "inset(0px 0px 0px 0px)" }, { clipPath: `inset(${p.top}px ${vw - p.right}px ${vh - p.bottom}px ${p.left}px)` }], {
					duration: EXIT_MS,
					easing: FOLD_EASE,
				});

				// the lockup lands on the sidebar's brand row, at the logo's height
				const box = lockup.current?.getBoundingClientRect();
				const mark = panel.querySelector<HTMLElement>("[data-brand-splash-mark]")?.getBoundingClientRect();
				if (box && mark && mark.height > 0) {
					const s = Math.min(1, (mark.height * 1.1) / box.height);
					const tx = mark.left - box.left;
					const ty = mark.top + mark.height / 2 - (box.height * s) / 2 - box.top;
					play(
						lockup.current,
						[
							{ transform: "none", opacity: 1 },
							{ opacity: 1, offset: 0.55 },
							{ transform: `translate(${tx}px, ${ty}px) scale(${s})`, opacity: 0 },
						],
						{ duration: EXIT_MS, easing: FOLD_EASE },
					);
				}
				last = play(el, [{ opacity: 1 }, { opacity: 0 }], { delay: EXIT_MS - 40, duration: FADE_MS });
			} else {
				play(lockup.current, [{ transform: "none" }, { transform: "scale(1.06)", opacity: 0 }], {
					duration: 420,
					easing: EASE_OUT,
				});
				last = play(el, [{ opacity: 1 }, { opacity: 0 }], { duration: 460, easing: "ease-in" });
			}
			if (last) last.onfinish = () => done.current();
			else done.current();
		};
		timer = window.setTimeout(exit, EXIT_AT);

		// any key or press skips to the fold
		window.addEventListener("keydown", exit);
		el.addEventListener("pointerdown", exit);
		return () => {
			window.clearTimeout(timer);
			window.removeEventListener("keydown", exit);
			el.removeEventListener("pointerdown", exit);
			for (const a of running) a.cancel();
		};
	}, []);

	const hidden: CSSProperties = { opacity: 0 };

	return (
		<div
			ref={root}
			aria-hidden="true"
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 1000,
				display: "grid",
				placeItems: "center",
				cursor: "pointer",
				background: `radial-gradient(120% 90% at 88% 0%, ${t.glow}, transparent 62%), linear-gradient(135deg, #0a1640, #040a1e 70%)`,
			}}
		>
			<div ref={navy} style={{ position: "absolute", inset: 0, opacity: 0 }} />
			<div ref={lockup} style={{ position: "relative", width: "min(46rem, 86vw)", transformOrigin: "0 0", ...hidden }}>
				<svg
					viewBox={`${t.view.x} ${t.view.y} ${t.view.w} ${t.view.h}`}
					style={{ display: "block", width: "100%", overflow: "visible" }}
				>
					<defs>
						<linearGradient
							id={`${uid}-fill`}
							gradientUnits="userSpaceOnUse"
							x1={RIBBON.x}
							y1={RIBBON.y}
							x2={RIBBON.x + RIBBON.w}
							y2={RIBBON.y + RIBBON.h}
						>
							{t.stops.map(([o, c]) => (
								<stop key={o} offset={o} stopColor={c} />
							))}
						</linearGradient>
						<radialGradient id={`${uid}-hl`} gradientUnits="userSpaceOnUse" cx={1235} cy={160} r={210}>
							<stop offset={0} stopColor={t.highlight} />
							<stop offset={1} stopColor={t.highlight} stopOpacity={0} />
						</radialGradient>
						<linearGradient id={`${uid}-fold`} gradientUnits="userSpaceOnUse" x1={0} y1={6} x2={0} y2={22}>
							<stop offset={0} stopColor={t.fold[0]} />
							<stop offset={1} stopColor={t.fold[1]} />
						</linearGradient>
						<linearGradient id={`${uid}-sheen`} x1={0} x2={1} y1={0} y2={0}>
							<stop offset={0} stopColor="#fff" stopOpacity={0} />
							<stop offset={0.5} stopColor="#fff" stopOpacity={0.6} />
							<stop offset={1} stopColor="#fff" stopOpacity={0} />
						</linearGradient>
						<clipPath id={`${uid}-mark`}>
							<path d={MARK} />
						</clipPath>
					</defs>

					<g transform={`translate(${t.shift.x} ${t.shift.y})`}>
						<g ref={fill} style={{ clipPath: "inset(0 100% 0 0)" }}>
							<path d={MARK} fill={`url(#${uid}-fill)`} />
							<path d={MARK} fill={`url(#${uid}-hl)`} />
							<path transform={FOLD_AT} d={FOLD} fill={`url(#${uid}-fold)`} />
						</g>
						<g clipPath={`url(#${uid}-mark)`}>
							<g transform={`skewX(-20) translate(${RIBBON.x - 380} 0)`}>
								<rect
									ref={sheen}
									x={0}
									y={RIBBON.y - 20}
									width={300}
									height={RIBBON.h + 40}
									fill={`url(#${uid}-sheen)`}
								/>
							</g>
						</g>
						<path
							ref={line}
							d={MARK}
							pathLength={1}
							fill="none"
							stroke={t.line}
							strokeWidth={5}
							strokeLinejoin="round"
							strokeDasharray={1}
							strokeDashoffset={1}
						/>
					</g>
					<image ref={word} href={wordSrc} x={t.word.x} y={t.word.y} width={t.word.w} height={t.word.h} style={hidden} />
					<image
						ref={tagline}
						href={taglineSrc}
						x={t.tagline.x}
						y={t.tagline.y}
						width={t.tagline.w}
						height={t.tagline.h}
						style={hidden}
					/>
				</svg>
			</div>
		</div>
	);
}
